import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { servicesApi, dataApi, transfersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Send,
  ArrowRight,
  Loader2,
  Home,
  History,
  Search,
  ChevronRight,
  Building2,
  Zap,
  Globe,
  Wallet,
  BadgeCheck,
  User,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  CheckCircle,
  DollarSign,
  Bitcoin
} from 'lucide-react';

export default function SendPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feeData, setFeeData] = useState(null);
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  
  const [formData, setFormData] = useState({
    service_type: searchParams.get('service')?.toLowerCase() || '',
    recipient_name: '',
    recipient_phone: '',
    recipient_email: '',
    recipient_country: 'US',
    recipient_bank_name: '',
    recipient_account_number: '',
    recipient_routing_number: '',
    recipient_iban: '',
    recipient_swift_code: '',
    amount: '',
    source_currency: 'USD',
    destination_currency: 'USD',
    description: ''
  });

  const serviceIcons = {
    swift: <Building2 className="w-6 h-6" />,
    moneygram: <Zap className="w-6 h-6" />,
    western_union: <Globe className="w-6 h-6" />,
    wire_transfer: <Send className="w-6 h-6" />,
    ach: <Wallet className="w-6 h-6" />,
    sepa: <BadgeCheck className="w-6 h-6" />,
    instant: <Zap className="w-6 h-6" />,
    crypto_bridge: <Bitcoin className="w-6 h-6" />
  };

  const serviceColors = {
    swift: '#3b82f6',
    moneygram: '#ef4444',
    western_union: '#fbbf24',
    wire_transfer: '#8b5cf6',
    ach: '#10b981',
    sepa: '#0ea5e9',
    instant: '#f59e0b',
    crypto_bridge: '#f97316'
  };

  const fetchData = async () => {
    try {
      const [servicesRes, currenciesRes, countriesRes] = await Promise.all([
        servicesApi.getAll(),
        dataApi.getCurrencies(),
        dataApi.getCountries()
      ]);
      setServices(servicesRes.data);
      setCurrencies(currenciesRes.data);
      setCountries(countriesRes.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateFee = async () => {
    try {
      const res = await servicesApi.calculateFee(formData.service_type, parseFloat(formData.amount));
      setFeeData(res.data);
    } catch (error) {
      console.error('Failed to calculate fee:', error);
    }
  };

  const convertCurrency = async () => {
    if (formData.source_currency === formData.destination_currency) {
      setConvertedAmount(parseFloat(formData.amount) || 0);
      return;
    }
    try {
      const res = await servicesApi.convertCurrency(
        parseFloat(formData.amount) || 0,
        formData.source_currency,
        formData.destination_currency
      );
      setConvertedAmount(res.data.to_amount);
    } catch (error) {
      console.error('Failed to convert currency:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.service_type && formData.amount && parseFloat(formData.amount) > 0) {
      calculateFee();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.service_type, formData.amount]);

  useEffect(() => {
    if (formData.amount && formData.source_currency && formData.destination_currency) {
      convertCurrency();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.amount, formData.source_currency, formData.destination_currency]);

  const handleServiceSelect = (serviceType) => {
    setFormData({ ...formData, service_type: serviceType });
    setStep(2);
  };

  const handleNext = () => {
    if (step === 2) {
      if (!formData.recipient_name || !formData.recipient_phone || !formData.recipient_country) {
        toast.error('Please fill in all required fields');
        return;
      }
    }
    if (step === 3) {
      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      if (feeData && feeData.total > (user?.balance || 0)) {
        toast.error(`Insufficient balance. Required: $${feeData.total.toFixed(2)}`);
        return;
      }
    }
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await transfersApi.create({
        ...formData,
        amount: parseFloat(formData.amount)
      });
      
      toast.success(`Transfer created! Tracking: ${res.data.tracking_number}`);
      await refreshUser();
      navigate(`/track?number=${res.data.tracking_number}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedService = services.find(s => s.code.toLowerCase() === formData.service_type);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900/20 via-green-900/10 to-teal-900/20 flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.5, opacity: 0, rotate: -180 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center"
        >
          <motion.div
            animate={{ 
              boxShadow: [
                '0 0 20px rgba(16, 185, 129, 0.3)',
                '0 0 40px rgba(16, 185, 129, 0.6)',
                '0 0 20px rgba(16, 185, 129, 0.3)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-4"
          >
            <Send className="w-10 h-10 text-white" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mx-auto" />
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900/20 via-green-900/10 to-teal-900/20" data-testid="send-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900/80 backdrop-blur-sm border-r border-emerald-500/20 p-6 hidden lg:flex flex-col shadow-lg">
        <Link to="/dashboard" className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
            <Send className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-emerald-100 font-manrope">MoneyMove</span>
        </Link>

        <nav className="flex-1 space-y-2">
          <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-emerald-200 hover:bg-emerald-500/10 hover:text-emerald-50 transition-colors">
            <Home className="w-5 h-5 text-emerald-400" />
            <span className="font-medium">Dashboard</span>
          </Link>
          <Link to="/send" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-500/10 text-emerald-100 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors">
            <Send className="w-5 h-5 text-emerald-400" />
            <span className="font-medium">Send Money</span>
          </Link>
          <Link to="/transactions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-emerald-200 hover:bg-emerald-500/10 hover:text-emerald-50 transition-colors">
            <History className="w-5 h-5 text-emerald-400" />
            <span className="font-medium">Transactions</span>
          </Link>
          <Link to="/track" className="flex items-center gap-3 px-4 py-3 rounded-lg text-emerald-200 hover:bg-emerald-500/10 hover:text-emerald-50 transition-colors">
            <Search className="w-5 h-5 text-emerald-400" />
            <span className="font-medium">Track Transfer</span>
          </Link>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8 pb-24 lg:pb-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" className="text-emerald-400 hover:text-emerald-300">
              <ChevronRight className="w-5 h-5 rotate-180" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-emerald-50 font-manrope">Send Money</h1>
            <p className="text-emerald-300/70">Choose a service and recipient</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-4 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= s ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-emerald-600'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 4 && <div className={`w-12 h-0.5 ml-2 ${step > s ? 'bg-emerald-500' : 'bg-slate-700'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Select Service */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <h2 className="text-xl font-bold text-emerald-50 mb-6 font-manrope">Select Transfer Method</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((service, index) => (
                <motion.button
                  key={service.code}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleServiceSelect(service.code.toLowerCase())}
                  className={`bg-slate-900/40 backdrop-blur-sm border rounded-xl p-5 text-left transition-all ${formData.service_type === service.code.toLowerCase() ? 'border-emerald-500 shadow-lg' : 'border-emerald-500/20 hover:border-emerald-500/40'} ${formData.service_type === service.code.toLowerCase() ? 'shadow-lg' : 'shadow-sm'}`}
                  data-testid={`service-${service.code.toLowerCase()}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center shadow-md">
                      {serviceIcons[service.code.toLowerCase()] || <Send className="w-6 h-6" />}
                    </div>
                    <span className="text-emerald-400/60 text-sm">
                      {service.min_days === service.max_days && service.min_days === 0 
                        ? 'Instant' 
                        : `${service.min_days}-${service.max_days} days`}
                    </span>
                  </div>
                  <h3 className="text-emerald-50 font-bold text-lg mb-1">{service.name}</h3>
                  <p className="text-emerald-400/60 text-sm mb-3">{service.description}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-emerald-400/60">Fee: {service.fee_percentage}% + ${service.flat_fee}</span>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 2: Recipient Details */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="max-w-2xl"
          >
            <h2 className="text-xl font-bold text-white mb-6">Recipient Details</h2>
            
            <div className="bg-slate-900/40 backdrop-blur-sm border border-emerald-500/20 rounded-xl p-6 space-y-5 shadow-lg">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-emerald-300">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-600" />
                    <Input
                      placeholder="John Doe"
                      value={formData.recipient_name}
                      onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                      className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 pl-10 h-12 focus:border-emerald-400 focus:ring-emerald-400/20"
                      data-testid="recipient-name-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-emerald-300">Phone Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-600" />
                    <Input
                      placeholder="+1 234 567 8900"
                      value={formData.recipient_phone}
                      onChange={(e) => setFormData({ ...formData, recipient_phone: e.target.value })}
                      className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 pl-10 h-12 focus:border-emerald-400 focus:ring-emerald-400/20"
                      data-testid="recipient-phone-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-emerald-300">Email (Optional)</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-600" />
                    <Input
                      type="email"
                      placeholder="recipient@email.com"
                      value={formData.recipient_email}
                      onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                      className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 pl-10 h-12 focus:border-emerald-400 focus:ring-emerald-400/20"
                      data-testid="recipient-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-emerald-300">Country *</Label>
                  <div className="space-y-2">
                    <Input
                      type="text"
                      placeholder="Search country..."
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-10 text-sm focus:border-emerald-400 focus:ring-emerald-400/20"
                    />
                    <Select 
                      value={formData.recipient_country} 
                      onValueChange={(value) => {
                        setFormData({ ...formData, recipient_country: value });
                        setCountrySearch('');
                      }}
                    >
                      <SelectTrigger className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-12 focus:border-emerald-400 focus:ring-emerald-400/20" data-testid="recipient-country-select">
                        <MapPin className="w-5 h-5 text-emerald-600 mr-2" />
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-emerald-500/30 max-h-[300px]">
                        {countries
                          .filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()))
                          .map((c) => (
                            <SelectItem key={c.code} value={c.code} className="text-emerald-50 hover:bg-emerald-500/10">
                              <span className="flex items-center gap-2">
                                <span>{c.flag}</span>
                                <span>{c.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Bank details for SWIFT/Wire/SEPA */}
              {['swift', 'wire_transfer', 'sepa', 'ach'].includes(formData.service_type) && (
                <>
                  <div className="border-t border-emerald-500/20 pt-5 mt-5">
                    <h3 className="text-emerald-50 font-semibold mb-4 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-emerald-400" />
                      Bank Details
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-emerald-300">Bank Name</Label>
                        <Input
                          placeholder="Bank of America"
                          value={formData.recipient_bank_name}
                          onChange={(e) => setFormData({ ...formData, recipient_bank_name: e.target.value })}
                          className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-12 focus:border-emerald-400 focus:ring-emerald-400/20"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-emerald-300">Account Number</Label>
                        <Input
                          placeholder="1234567890"
                          value={formData.recipient_account_number}
                          onChange={(e) => setFormData({ ...formData, recipient_account_number: e.target.value })}
                          className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-12 focus:border-emerald-400 focus:ring-emerald-400/20"
                        />
                      </div>
                    </div>
                    
                    {formData.service_type === 'swift' && (
                      <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                          <Label className="text-emerald-300">SWIFT/BIC Code</Label>
                          <Input
                            placeholder="BOFAUS3N"
                            value={formData.recipient_swift_code}
                            onChange={(e) => setFormData({ ...formData, recipient_swift_code: e.target.value })}
                            className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-12 focus:border-emerald-400 focus:ring-emerald-400/20"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-emerald-300">IBAN (if applicable)</Label>
                          <Input
                            placeholder="DE89370400440532013000"
                            value={formData.recipient_iban}
                            onChange={(e) => setFormData({ ...formData, recipient_iban: e.target.value })}
                            className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-12 focus:border-emerald-400 focus:ring-emerald-400/20"
                          />
                        </div>
                      </div>
                    )}
                    
                    {formData.service_type === 'ach' && (
                      <div className="space-y-2 mt-4">
                        <Label className="text-emerald-300">Routing Number</Label>
                        <Input
                          placeholder="021000021"
                          value={formData.recipient_routing_number}
                          onChange={(e) => setFormData({ ...formData, recipient_routing_number: e.target.value })}
                          className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-12 focus:border-emerald-400 focus:ring-emerald-400/20"
                        />
                      </div>
                    )}
                    
                    {formData.service_type === 'sepa' && (
                      <div className="space-y-2 mt-4">
                        <Label className="text-emerald-300">IBAN</Label>
                        <Input
                          placeholder="DE89370400440532013000"
                          value={formData.recipient_iban}
                          onChange={(e) => setFormData({ ...formData, recipient_iban: e.target.value })}
                          className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-12 focus:border-emerald-400 focus:ring-emerald-400/20"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-12 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                  Back
                </Button>
                <Button onClick={handleNext} className="flex-1 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold shadow-lg">
                  Continue
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Amount */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="max-w-2xl"
          >
            <h2 className="text-xl font-bold text-white mb-6">Transfer Amount</h2>
            
            <div className="bg-slate-900/40 backdrop-blur-sm border border-emerald-500/20 rounded-xl p-6 space-y-5 shadow-lg">
              <div className="space-y-2">
                <Label className="text-emerald-300">You Send</Label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 text-2xl h-14 font-mono focus:border-emerald-400 focus:ring-emerald-400/20"
                      data-testid="amount-input"
                    />
                  </div>
                  <Select 
                    value={formData.source_currency} 
                    onValueChange={(value) => setFormData({ ...formData, source_currency: value })}
                  >
                    <SelectTrigger className="w-32 bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-14 focus:border-emerald-400 focus:ring-emerald-400/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-emerald-500/30">
                      {currencies.map((c) => (
                        <SelectItem key={c.code} value={c.code} className="text-emerald-50 hover:bg-emerald-500/10">
                          {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-emerald-300">Recipient Gets</Label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Input
                      type="text"
                      value={convertedAmount?.toFixed(2) || '0.00'}
                      disabled
                      className="bg-slate-800/50 border-emerald-500/30 text-emerald-400 text-2xl h-14 font-mono"
                    />
                  </div>
                  <Select 
                    value={formData.destination_currency} 
                    onValueChange={(value) => setFormData({ ...formData, destination_currency: value })}
                  >
                    <SelectTrigger className="w-32 bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-14 focus:border-emerald-400 focus:ring-emerald-400/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-emerald-500/30">
                      {currencies.map((c) => (
                        <SelectItem key={c.code} value={c.code} className="text-emerald-50 hover:bg-emerald-500/10">
                          {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Fee breakdown */}
              {feeData && (
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-2 border border-emerald-500/20">
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-400/60">Transfer Fee</span>
                    <span className="text-emerald-50">${feeData.fee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-emerald-500/20 pt-2">
                    <span className="text-emerald-400/60">Total to Pay</span>
                    <span className="text-emerald-400 font-bold">${feeData.total.toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-emerald-300">Description (Optional)</Label>
                <Input
                  placeholder="Payment for..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-12 focus:border-emerald-400 focus:ring-emerald-400/20"
                />
              </div>

              <div className="bg-slate-800/50 rounded-lg p-4 border border-emerald-500/20">
                <p className="text-emerald-400/60 text-sm">
                  Your balance: <span className="text-emerald-50 font-bold">${user?.balance?.toFixed(2) || '0.00'}</span>
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1 h-12 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                  Back
                </Button>
                <Button onClick={handleNext} className="flex-1 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold shadow-lg">
                  Review Transfer
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Review & Confirm */}
        {step === 4 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="max-w-2xl"
          >
            <h2 className="text-xl font-bold text-white mb-6">Review Transfer</h2>
            
            <div className="bg-slate-900/40 backdrop-blur-sm border border-emerald-500/20 rounded-xl p-6 shadow-lg">
              {/* Service Info */}
              <div className="flex items-center gap-4 pb-6 border-b border-emerald-500/20">
                <div className="w-14 h-14 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center shadow-md">
                  {serviceIcons[formData.service_type] || <Send className="w-7 h-7" />}
                </div>
                <div>
                  <p className="text-emerald-50 font-bold text-lg">{selectedService?.name}</p>
                  <p className="text-emerald-400/60">Est. delivery: {selectedService?.min_days === selectedService?.max_days && selectedService?.min_days === 0 ? 'Instant' : `${selectedService?.min_days}-${selectedService?.max_days} business days`}</p>
                </div>
              </div>

              {/* Amount */}
              <div className="py-6 border-b border-emerald-500/20 text-center">
                <p className="text-emerald-400/60 text-sm mb-2">Recipient Gets</p>
                <p className="text-4xl font-bold text-emerald-400 font-mono">
                  {convertedAmount?.toFixed(2)} {formData.destination_currency}
                </p>
              </div>

              {/* Details */}
              <div className="py-6 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-emerald-400/60">Recipient</span>
                  <span className="text-emerald-50 font-medium">{formData.recipient_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-emerald-400/60">Phone</span>
                  <span className="text-emerald-50">{formData.recipient_phone}</span>
                </div>
                {formData.recipient_email && (
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-400/60">Email</span>
                    <span className="text-emerald-50">{formData.recipient_email}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-emerald-400/60">Country</span>
                  <span className="text-emerald-50 flex items-center gap-2">
                    {countries.find(c => c.code === formData.recipient_country)?.flag}
                    {countries.find(c => c.code === formData.recipient_country)?.name}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-emerald-400/60">You Send</span>
                  <span className="text-emerald-50 font-mono">{parseFloat(formData.amount).toLocaleString()} {formData.source_currency}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-emerald-400/60">Fee</span>
                  <span className="text-emerald-50 font-mono">{feeData?.fee.toFixed(2) || '0.00'} {formData.source_currency}</span>
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-emerald-500/20">
                  <span className="text-emerald-50 font-bold">Total</span>
                  <span className="text-emerald-400 font-bold text-xl font-mono">{feeData?.total.toFixed(2) || '0.00'} {formData.source_currency}</span>
                </div>
              </div>

              {/* Notification Info */}
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 mb-4">
                <p className="text-emerald-400 text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {formData.recipient_email 
                    ? `Recipient will be notified via SMS and Email` 
                    : `Recipient will be notified via SMS`}
                </p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(3)} className="flex-1 h-12 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitting}
                  className="flex-1 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold shadow-lg"
                  data-testid="confirm-transfer-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                  ) : (
                    <>
                      Confirm & Send
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </main>

      {/* Mobile Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-900/80 backdrop-blur-sm border-t border-emerald-500/20 p-4 shadow-lg">
        <div className="flex justify-around">
          <Link to="/dashboard" className="flex flex-col items-center text-emerald-600">
            <Home className="w-6 h-6" />
            <span className="text-xs mt-1 text-emerald-600 font-medium">Home</span>
          </Link>
          <Link to="/send" className="flex flex-col items-center text-emerald-400">
            <Send className="w-6 h-6" />
            <span className="text-xs mt-1 text-emerald-400 font-medium">Send</span>
          </Link>
          <Link to="/transactions" className="flex flex-col items-center text-emerald-600">
            <History className="w-6 h-6" />
            <span className="text-xs mt-1 text-emerald-600 font-medium">History</span>
          </Link>
          <Link to="/track" className="flex flex-col items-center text-emerald-600">
            <Search className="w-6 h-6" />
            <span className="text-xs mt-1 text-emerald-600 font-medium">Track</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
